/**
 * EndedOverlay — banner shown over the chat once the session reaches a
 * terminal verdict (close_won / close_lost).
 *
 * The chat transcript is preserved underneath; the user must click
 * "Ver relatório" to navigate (no auto-redirect — spec §5).
 */
import { Trophy, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { CloseVerdict, SimulatorStatus } from "../types";

export interface EndedOverlayProps {
  status: Extract<SimulatorStatus, "close_won" | "close_lost">;
  verdict?: CloseVerdict;
  onViewReport: () => void;
  onBackToSetup: () => void;
}

const VERDICT_LABEL: Record<CloseVerdict, string> = {
  accept: "Fechou",
  decline: "Recusou",
  delay: "Adiou",
};

export function EndedOverlay({
  status,
  verdict,
  onViewReport,
  onBackToSetup,
}: EndedOverlayProps) {
  const won = status === "close_won";
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-card border border-border rounded-xl p-6 max-w-md w-[90%] space-y-4 shadow-xl">
        <div className="flex items-center gap-3">
          {won ? (
            <Trophy weight="duotone" className="w-8 h-8 text-emerald-500" />
          ) : (
            <XCircle weight="duotone" className="w-8 h-8 text-destructive" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {won ? "Atendimento fechado!" : "Paciente recusou."}
            </h3>
            <Badge
              variant={won ? "default" : "destructive"}
              className="mt-1 text-[10px]"
            >
              {VERDICT_LABEL[verdict ?? (won ? "accept" : "decline")]}
            </Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {won
            ? "O paciente decidiu seguir com o procedimento. Veja o relatório completo do atendimento."
            : "O paciente decidiu não seguir nesta consulta. Veja onde a conversa quebrou no relatório."}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onViewReport} className="flex-1">
            Ver relatório
          </Button>
          <Button variant="outline" onClick={onBackToSetup} className="flex-1">
            Voltar à tela inicial
          </Button>
        </div>
      </div>
    </div>
  );
}
